/**
 * ==========================================================================
 *  The printed things: badges, registrations, sponsor prints and the smoke
 *  sprite. Drawn onto canvases at load time, like the tiles next door in
 *  `surfaces.ts`, and for the same three reasons - no image assets to ship,
 *  no licensing question over a logo, and the same pixels on every machine.
 *
 *  Kept apart from the tiles because they are a different kind of thing. A
 *  tile is a surface that repeats and is measured in metres of world per
 *  tile; these are one-off pictures with an aspect ratio, stuck to one face
 *  of one mesh, and the numbers that matter about them are their proportions
 *  rather than their scale.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { SIZE, dot, grain, shade, type Ctx } from './canvas';
import { PALETTE } from './surfaces';
import { mulberry32 } from '../utils/prng';

/* ------------------------------------------------------------- roundel --- */

/**
 * The badge on the bemari.
 *
 * Drawn rather than shipped, like everything else here - and drawn as the thing
 * it actually is: a black ring with the letters round the top, and a quartered
 * blue-and-white centre. The quarters are the Bavarian state colours, which is
 * also why getting them the right way round matters more than getting the blue
 * exactly right.
 *
 * The disc it goes on is a `CircleGeometry`, whose UVs map the circle to the
 * inscribed circle of the texture - so the roundel is drawn to `R` and the
 * corners of the canvas are never sampled.
 */
export function buildRoundel(): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const c = SIZE / 2;
    const R = SIZE / 2 - 4;

    // The black ring, with a bright rim so the badge catches the light.
    ctx.fillStyle = '#0a0a0c';
    ctx.beginPath();
    ctx.arc(c, c, R, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#dcdcdf';
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.arc(c, c, R - 3, 0, Math.PI * 2);
    ctx.stroke();

    // The quartered centre. Upper left white, then alternating round.
    const inner = R * 0.66;
    const quarters = ['#f2f4f5', '#0a63ac', '#f2f4f5', '#0a63ac'];
    for (let i = 0; i < 4; i++) {
      ctx.fillStyle = quarters[i];
      ctx.beginPath();
      ctx.moveTo(c, c);
      ctx.arc(c, c, inner, Math.PI + i * (Math.PI / 2), Math.PI + (i + 1) * (Math.PI / 2));
      ctx.closePath();
      ctx.fill();
    }
    ctx.strokeStyle = '#c8c8cc';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(c, c, inner, 0, Math.PI * 2);
    ctx.stroke();

    // The letters, set round the top of the ring the way they are on the car.
    ctx.fillStyle = '#f2f4f5';
    ctx.font = `bold ${Math.round(R * 0.3)}px Helvetica, Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const letters = 'BMW';
    for (let i = 0; i < letters.length; i++) {
      const angle = -Math.PI / 2 + (i - 1) * 0.42;
      ctx.save();
      ctx.translate(c + Math.cos(angle) * (R * 0.83), c + Math.sin(angle) * (R * 0.83));
      ctx.rotate(angle + Math.PI / 2);
      ctx.fillText(letters[i], 0, 0);
      ctx.restore();
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}


/* -------------------------------------------------------------- renkaat --- */

/**
 * Centre-to-centre spacing of the four rings, and the breathing room round the
 * outside, both in ring diameters. The badge is nothing but these two numbers:
 * get the spacing wrong and four circles in a row stop being a marque.
 */
const RING_GAP = 0.68;
const RING_PAD = 0.07;
/** Which makes the badge this much wider than it is tall. */
export const RINGS_ASPECT = (3 * RING_GAP + 1 + 2 * RING_PAD) / (1 + 2 * RING_PAD);

/**
 * The badge on the audi parked across the gap from the bemari.
 *
 * Drawn like the roundel and for the same reason, but on a plane rather than a
 * disc: four rings in a row are a wide, mostly empty shape, and the only honest
 * way to hang one on a bonnet is alpha. Everything outside the rings stays
 * transparent - see `mountRings`, which is the one place in the yard that draws
 * a badge with a hole in it.
 *
 * Each ring is stroked twice: a dark line under a bright one, so the chrome
 * reads against the car's own paint instead of dissolving into it. That matters
 * more here than on the roundel, which brings its own black field with it.
 */
export function buildRings(): THREE.Texture {
  const height = 128;
  const width = Math.round(height * RINGS_ASPECT);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const diameter = height / (1 + 2 * RING_PAD);
    const radius = diameter / 2;
    const line = diameter * 0.11;
    const first = RING_PAD * diameter + radius;

    ctx.lineCap = 'butt';
    for (let i = 0; i < 4; i++) {
      const x = first + i * RING_GAP * diameter;
      // The dark line goes down first and a hair wider, so what is left of it
      // after the bright one lands on top is an outline.
      for (const [colour, weight] of [
        ['#33363b', 1.4],
        ['#e6eaee', 1.0],
      ] as [string, number][]) {
        ctx.strokeStyle = colour;
        ctx.lineWidth = line * weight;
        ctx.beginPath();
        ctx.arc(x, height / 2, radius - line / 2, 0, Math.PI * 2);
        ctx.stroke();
      }
      // A highlight over the top of each ring: chrome is only chrome because
      // the sky is in it, and nothing here is going to reflect the sky.
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = line * 0.32;
      ctx.beginPath();
      ctx.arc(x, height / 2, radius - line * 0.72, Math.PI * 1.15, Math.PI * 1.85);
      ctx.stroke();
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}


/* ------------------------------------------------------------ rekkari --- */

/** Finnish plate proportions: 442 x 118 mm, i.e. a good deal squarer than EU. */
export const PLATE_ASPECT = 442 / 118;
/** Its size in the world, in metres. */
export const PLATE_SIZE = { width: 0.442, height: 0.118 } as const;

/**
 * A registration plate.
 *
 * Drawn rather than shipped, like the roundel, and drawn as a Finnish plate:
 * white field, black characters, and the blue EU band down the left with the
 * ring of stars over `FIN`. The band is the whole of what makes it read as a
 * plate at ten metres rather than as a white sticker - the letters are legible
 * for about two of those.
 *
 * The text is squeezed to fit rather than clipped. Finnish erikoistunnukset run
 * to seven characters, and a plate that quietly drops the last one of them is
 * worse than a slightly narrow font.
 */
export function buildPlate(text: string): THREE.Texture {
  const height = 128;
  const width = Math.round(height * PLATE_ASPECT);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    // The field, and the pressed black rim round it.
    ctx.fillStyle = '#f4f4f0';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#141414';
    ctx.lineWidth = height * 0.05;
    ctx.strokeRect(height * 0.05, height * 0.05, width - height * 0.1, height - height * 0.1);

    // The EU band, 40 mm of the plate's 442.
    const band = width * (40 / 442);
    ctx.fillStyle = '#0a3a9c';
    ctx.fillRect(0, 0, band, height);

    // Twelve stars, in a ring in the upper half of the band.
    ctx.fillStyle = '#f5d020';
    const starRadius = band * 0.28;
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      dot(
        ctx,
        band / 2 + Math.sin(angle) * starRadius,
        height * 0.34 - Math.cos(angle) * starRadius,
        band * 0.055,
      );
    }
    ctx.fillStyle = '#f4f4f0';
    ctx.font = `bold ${Math.round(height * 0.19)}px Helvetica, Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('FIN', band / 2, height * 0.78);

    // The registration itself, in the field to the right of the band.
    const field = { from: band, to: width - height * 0.12 };
    ctx.fillStyle = '#141414';
    ctx.font = `bold ${Math.round(height * 0.62)}px Helvetica, Arial, sans-serif`;
    const room = (field.to - field.from) * 0.88;
    const measured = ctx.measureText(text).width;
    ctx.save();
    ctx.translate((field.from + field.to) / 2, height * 0.54);
    if (measured > room) ctx.scale(room / measured, 1);
    ctx.fillText(text, 0, 0);
    ctx.restore();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}


/* ------------------------------------------------------------- teltta --- */

/**
 * The wordmark on the XLMOTO tent: the bracketed X, then the name.
 *
 * Drawn as paths and one run of text rather than shipped as a logo file, like
 * the roundel and the four rings and for the same three reasons - the CSP, the
 * licence, and the fact that the same seed has to draw the same pixels on
 * every machine. It is a *mark*, not a reproduction: at the size it is read at
 * - a 30 cm skirt going past at 30 km/h - what survives is the shape of the
 * brackets and the length of the word, and both of those are here.
 *
 * `height` is the cap height of the lettering. Everything else is a multiple
 * of it, so the mark scales as one thing.
 */
function wordmark(ctx: Ctx, cx: number, cy: number, height: number, colour: string): void {
  ctx.font = `bold ${Math.round(height)}px Helvetica, Arial, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  // The mark is a hair under a cap height wide, and the space after it is what
  // stops it reading as a seventh letter.
  const mark = height * 0.9;
  const gap = height * 0.34;
  const word = ctx.measureText('XLMOTO').width;
  const left = cx - (mark + gap + word) / 2;
  const mx = left + mark / 2;

  ctx.strokeStyle = colour;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Two chevrons facing each other. Their tips are what give the mark its
  // hexagonal silhouette, so they run the full height of the letters.
  ctx.lineWidth = height * 0.15;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(mx + side * height * 0.2, cy - height * 0.52);
    ctx.lineTo(mx + side * height * 0.45, cy);
    ctx.lineTo(mx + side * height * 0.2, cy + height * 0.52);
    ctx.stroke();
  }

  // The X between them, drawn short of the chevrons so the three shapes stay
  // three shapes.
  ctx.lineWidth = height * 0.16;
  for (const slope of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(mx - height * 0.17, cy - slope * height * 0.33);
    ctx.lineTo(mx + height * 0.17, cy + slope * height * 0.33);
    ctx.stroke();
  }

  ctx.fillStyle = colour;
  ctx.fillText('XLMOTO', left + mark + gap, cy + height * 0.02);
}

/**
 * How much wider than it is tall one repeat of the printed skirt is.
 *
 * The band round a pop-up's eave is one wordmark per stretch of about a metre,
 * and `World.buildTent` divides the eave by this to decide how many repeats go
 * on a side. A tile drawn at one aspect and repeated at another is a stretched
 * logo, so the two numbers come from here.
 */
export const TENT_BAND_ASPECT = 3.4;

/**
 * One repeat of the valance: the wordmark on the canopy's own black.
 *
 * Tiled horizontally by `World.buildTent` and never vertically, which is why
 * this is a strip rather than a `SIZE` square - the skirt is 30 cm deep and a
 * square tile would put a third of a logo on it.
 */
export function buildTentBand(): THREE.Texture {
  const height = 96;
  const width = Math.round(height * TENT_BAND_ASPECT);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = shade(PALETTE.tentCanopy);
    ctx.fillRect(0, 0, width, height);
    // The hem, which is the seam the skirt is sewn along and the one line that
    // says this is a printed band rather than a painted stripe.
    ctx.fillStyle = shade(PALETTE.tentCanopy, 1.55);
    ctx.fillRect(0, height - 3, width, 2);
    wordmark(ctx, width / 2, height * 0.47, height * 0.5, '#f2f3f5');
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  return texture;
}

/** A printed wall panel is this much wider than the logo across it is tall. */
export const TENT_LOGO_ASPECT = 3.4;

/**
 * The big mark across a side wall.
 *
 * Alpha, and hung a centimetre proud of the sheeting rather than drawn into
 * it - the same trick as the audi's rings, and for the same reason: the walls
 * are one tiled material shared by all four sides, and a logo baked into that
 * tile would print itself every 35 cm.
 *
 * Grey rather than black, because this is a print on white PE seen through the
 * shade under a canopy, and a hard black panel there reads as a hole in the
 * tent.
 */
export function buildTentLogo(): THREE.Texture {
  const height = 128;
  const width = Math.round(height * TENT_LOGO_ASPECT);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx) wordmark(ctx, width / 2, height / 2, height * 0.62, 'rgba(64,66,70,0.82)');

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}


/* --------------------------------------------------------- leiriteltat --- */

/**
 * The flysheet a camping tent is cut from, drawn white so that the colour can
 * come from the tent instead of from the tile.
 *
 * Every other surface in the level is drawn in its own palette colour and
 * tinted by nothing - see `material()`, which says so out loud. This one is
 * the exception, and the reason is the camp: thirty-odd tents in a field are
 * thirty-odd colours, and the alternative to one white tile multiplied by a
 * colour is thirty palette entries and thirty canvases of the same nylon.
 * `World.campTentMaterial` owns that multiplication and caches one material
 * per colour.
 *
 * The tile is the seams and almost nothing else. A fly is welded panels, and
 * from across a field the seam is the only thing on one the eye can find -
 * everything finer than that is gone by the second row of tents.
 *
 * No `SIZE` square and no entry in `RECIPES`, for the same reason the printed
 * band above is neither: the UVs this is stretched over are the tent's own
 * (once round the section, once along the ridge), so there is no tile size to
 * agree with `worldSize` and nothing to agree it with.
 */
export function buildCampFly(): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const white = 0xffffff;
    const rng = mulberry32(0x7e17a);
    // Panels across the fly rather than along it: the canvas's y is the axis
    // the UVs run up, which on a pitched tent is the ridge - so a band here is
    // a panel sewn over the top and down both sides, which is how a fly is
    // actually made.
    ctx.fillStyle = shade(white, 0.94);
    ctx.fillRect(0, 0, SIZE, SIZE);
    const panels = 5;
    const pitch = SIZE / panels;
    for (let i = 0; i < panels; i++) {
      // Coated nylon takes the light differently panel to panel, and that
      // difference is most of what stops a tinted tent reading as plastic.
      ctx.fillStyle = shade(white, 0.9 + rng() * 0.1);
      ctx.fillRect(0, i * pitch, SIZE, pitch - 2);
      ctx.fillStyle = shade(white, 0.74);
      ctx.fillRect(0, i * pitch + pitch - 2, SIZE, 2);
    }
    grain(ctx, rng, 6);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  return texture;
}


/* --------------------------------------------------------------- savu --- */

/**
 * One puff of smoke, for the kamiina that heats the palju.
 *
 * A single sprite drawn once and shared by every puff in the yard: what makes
 * a plume read as a plume is the *motion* - a puff growing, thinning and
 * leaning downwind as it climbs - and that is animation, not texture. See
 * `World.renderSmoke`.
 *
 * Alpha only, so the sprite's own colour decides whether this is birch smoke
 * off a hot fire or the grey of one somebody has just thrown a damp log into.
 */
export function buildSmokePuff(): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const c = SIZE / 2;

    // A soft core with a long tail. The tail is the whole job: a puff with an
    // edge on it reads as a cotton ball glued to the sky.
    const gradient = ctx.createRadialGradient(c, c, 0, c, c, c);
    gradient.addColorStop(0, 'rgba(255,255,255,0.92)');
    gradient.addColorStop(0.42, 'rgba(255,255,255,0.44)');
    gradient.addColorStop(0.76, 'rgba(255,255,255,0.12)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, SIZE, SIZE);

    // Bitten out of the core, so fourteen copies of one sprite are not
    // fourteen identical circles. Shallow bites only - anything deeper turns
    // into a hole you can see the sky through.
    const rng = mulberry32(0x5a0f7e);
    ctx.globalCompositeOperation = 'destination-out';
    for (let i = 0; i < 36; i++) {
      const angle = rng() * Math.PI * 2;
      const distance = c * (0.18 + rng() * 0.5);
      ctx.fillStyle = `rgba(0,0,0,${(0.05 + rng() * 0.11).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(
        c + Math.cos(angle) * distance,
        c + Math.sin(angle) * distance,
        c * (0.07 + rng() * 0.15),
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
